import type { ComuneNodo } from '@/api/types';

/**
 * Funzioni PURE sull'elenco dei comuni. Nessun dato qui dentro.
 * ---------------------------------------------------------------------------
 * Fino al 01/09/2026 questo modulo ERA la fonte dei dati: importava
 * `data/comuni-nodes.json`, 257 record estratti dal portale statico e
 * compilati nel bundle. Il risultato è che dropdown «Comuni», picker
 * dell'hero e mappa mostravano 257 comuni anche con la base dati vuota, e
 * ogni clic portava a «Comune non trovato». I dati arrivano ora dall'API
 * (`listNodiComuni`, condivisa da `useComuni`): qui restano solo i
 * raggruppamenti e le ricerche, che non hanno stato e si testano da sole.
 *
 * Il tipo `ComuneNode` non esiste più: la riga su cui si lavora è quella del
 * contratto, `ComuneNodo`. Un tipo parallelo avrebbe voluto dire tradurre
 * la risposta in una forma nostra a ogni chiamata, per niente.
 *
 * Le costanti delle province restano STATICHE ed è corretto: sono un elenco
 * amministrativo chiuso, non un dato editoriale (stessa ragione di
 * `lib/province.ts`, che è la fonte per le rotte `/province/<slug>/`).
 */

/** Nomi estesi delle province, in ordine alfabetico di sigla. */
export const PROVINCE_NAMES: Record<string, string> = {
  BA: 'Bari',
  BAT: 'Barletta-Andria-Trani',
  BR: 'Brindisi',
  FG: 'Foggia',
  LE: 'Lecce',
  TA: 'Taranto',
};

/** Ordine di presentazione delle sigle (quello del riferimento). */
export const PROVINCE_SIGLE: readonly string[] = ['BA', 'BAT', 'BR', 'FG', 'LE', 'TA'];

/** Slug della pagina provincia (`/province/<slug>/`) per sigla. */
export const PROVINCE_SLUGS: Record<string, string> = {
  BA: 'bari',
  BAT: 'barletta-andria-trani',
  BR: 'brindisi',
  FG: 'foggia',
  LE: 'lecce',
  TA: 'taranto',
};

/**
 * Un comune CON la sua proiezione sulla sagoma: `x` e `y` non sono più
 * opzionali né nulli. È il tipo che la mappa può disegnare senza controlli
 * sparsi in mezzo al JSX.
 */
export type NodoMappa = ComuneNodo & { readonly x: number; readonly y: number };

/** Comuni raggruppati per sigla provincia, ordinati per nome. */
export function comuniPerProvincia(
  comuni: readonly ComuneNodo[],
): ReadonlyMap<string, readonly ComuneNodo[]> {
  const gruppi = new Map<string, ComuneNodo[]>();
  for (const comune of comuni) {
    const lista = gruppi.get(comune.provincia) ?? [];
    lista.push(comune);
    gruppi.set(comune.provincia, lista);
  }
  for (const lista of gruppi.values()) {
    lista.sort((a, b) => a.nome.localeCompare(b.nome, 'it'));
  }
  return gruppi;
}

/**
 * I capoluoghi, ordinati per nome: il default del picker dell'hero.
 *
 * Il rango arriva dall'API (`capoluogo`). Se NESSUNA riga lo dichiara —
 * fixture del dataset storico, o base dati senza il flag — il risultato è
 * vuoto, e chi chiama deve dirlo invece di dedurlo: il capoluogo non si
 * indovina dal nome della provincia (BAT ne ha tre e non si chiama come
 * nessuno di loro).
 */
export function capoluoghi(comuni: readonly ComuneNodo[]): readonly ComuneNodo[] {
  return comuni
    .filter((comune) => comune.capoluogo === true)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'it'));
}

// Rimuove i segni diacritici combinanti (U+0300–U+036F) dopo NFD.
function normalizza(testo: string): string {
  return testo
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * Ricerca per sottostringa (case/accents-insensitive) con filtro provincia
 * opzionale. Stessa filosofia di CercaPage: match semplice e spiegabile.
 *
 * Con `query` vuota non filtra per testo: serve anche a prendere «i primi N
 * comuni», che è ciò che il picker mostra a riposo quando i capoluoghi non
 * sono dichiarati da nessuno.
 */
export function cercaComuni(
  comuni: readonly ComuneNodo[],
  query: string,
  provincia: string | null,
  limite = 12,
): readonly ComuneNodo[] {
  const q = normalizza(query.trim());
  const risultati: ComuneNodo[] = [];
  for (const comune of comuni) {
    if (provincia && comune.provincia !== provincia) continue;
    if (q && !normalizza(comune.nome).includes(q)) continue;
    risultati.push(comune);
    if (risultati.length >= limite) break;
  }
  return risultati.sort((a, b) => a.nome.localeCompare(b.nome, 'it'));
}

/**
 * I soli comuni disegnabili sulla sagoma.
 *
 * Un comune senza proiezione viene OMESSO: disegnarlo a (0,0) metterebbe un
 * punto vero in un posto falso, nell'angolo del viewBox. `x`/`y` a `null`
 * (dichiarati assenti) e `undefined` (la sorgente non ha il campo) portano
 * allo stesso esito — non c'è niente da disegnare in nessuno dei due casi.
 */
export function nodiMappa(comuni: readonly ComuneNodo[]): readonly NodoMappa[] {
  const nodi: NodoMappa[] = [];
  for (const comune of comuni) {
    const { x, y } = comune;
    if (typeof x !== 'number' || !Number.isFinite(x)) continue;
    if (typeof y !== 'number' || !Number.isFinite(y)) continue;
    nodi.push({ ...comune, x, y });
  }
  return nodi;
}
